import { ChartBarIcon, CreditCardIcon, HouseIcon, PiggyBankIcon } from '@phosphor-icons/react'
import { motion } from 'motion/react'
import { NavLink } from 'react-router'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Navegación inferior flotante.
 *
 * Cápsula despegada del borde en vez de barra pegada abajo: deja ver que el
 * contenido continúa por detrás y aleja los blancos del indicador de gestos del
 * iPhone, donde un toque se pierde con facilidad.
 *
 * Solo el destino activo muestra su nombre. Cuatro etiquetas simultáneas
 * obligan a leer la barra entera para saber dónde se está; con una sola, la
 * respuesta es inmediata y sobra espacio para que cada blanco sea cómodo.
 *
 * Registrar un gasto va **al lado** de la cápsula, no dentro: la cápsula agrupa
 * lugares a los que se va, y esto es una acción que se hace. Comparten fila
 * para que floten como una sola pieza y para que la separación entre ambos la
 * calcule el propio flexbox — dos elementos fijos por su cuenta se solapan en
 * cuanto la pantalla es estrecha.
 */

const TABS = [
  { to: '/', label: 'Inicio', Icon: HouseIcon },
  { to: '/movimientos', label: 'Movimientos', Icon: ChartBarIcon },
  { to: '/tarjetas', label: 'Tarjetas', Icon: CreditCardIcon },
  { to: '/ahorro', label: 'Ahorro', Icon: PiggyBankIcon },
] as const

export function TabBar() {
  return (
    <nav
      aria-label="Navegación principal"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-safe"
    >
      {/* Cápsula de tinta, no de superficie.
          Sobre papel marfil una cápsula marfil se apoya en la sombra para
          existir, y la sombra es justo lo que delata una barra web. En tinta
          flota sola y además repite el bloque que abre la pantalla: la app queda
          sujeta por arriba y por abajo con el mismo color. */}
      <ul
        className={cn(
          'pointer-events-auto mb-2 flex min-w-0 items-center gap-1 rounded-pill',
          'bg-navy/95 p-1.5 shadow-nav backdrop-blur-xl',
        )}
      >
        {TABS.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              end={tab.to === '/'}
              onClick={() => haptic('selection')}
              className="block"
            >
              {({ isActive }) => (
                <span
                  className={cn(
                    'relative flex min-h-[48px] items-center justify-center gap-2 rounded-pill',
                    'transition-[padding] duration-200',
                    isActive ? 'px-4' : 'w-12',
                  )}
                >
                  {isActive ? (
                    // `layoutId` desliza la pastilla entre destinos en vez de
                    // hacerla reaparecer: el movimiento explica de dónde vino.
                    <motion.span
                      layoutId="tab-pill"
                      className="absolute inset-0 rounded-pill bg-brand"
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                    />
                  ) : null}
                  <tab.Icon
                    size={21}
                    weight={isActive ? 'fill' : 'regular'}
                    className={cn(
                      'relative z-10',
                      isActive ? 'text-on-brand' : 'text-on-navy-soft',
                    )}
                  />
                  {isActive ? (
                    <span className="relative z-10 whitespace-nowrap text-[13px] font-bold text-on-brand">
                      {tab.label}
                    </span>
                  ) : (
                    // El nombre sigue disponible para lectores de pantalla
                    // aunque no se dibuje: prescindir de él dejaría cuatro
                    // botones sin identificar.
                    <span className="sr-only">{tab.label}</span>
                  )}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
